import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, timeout } from 'rxjs';
import { CoverTemplateEntity } from 'src/app/domain/entities/partials/cover-template/cover-template.entity';
import { environment } from 'src/environments/environment';

import {
    applySvgColorModifications,
    coverBackgroundPositionCss,
    coverBackgroundSizeCss,
    CoverRendererTemplate,
    renderCoverItemsHtml,
} from '../preview/components/covers/cover-renderer';

/**
 * Monta o fragmento da capa PARA O PDF, no navegador.
 *
 * Espelho do `buildCoverFragment` da API (`cover-preview.service.ts`): mesmos itens, mesmo
 * bloco de estilos (`pdfConfigs`) e mesmo `<div class="container-cover">` com o fundo. Os dois
 * bebem do MESMO renderer puro (`cover-renderer.ts`), que é a fonte única do layout da capa,
 * então o que o painel desenha é o que o PDF imprime.
 *
 * Existe no cliente porque o painel do editor monta a prévia inteira localmente: pedir o
 * fragmento à API a cada tecla digitada custaria uma ida ao servidor por rebuild. O caminho do
 * PDF de verdade (a página de prévia e o PDF que o app gera) continua usando o
 * `renderedCoverHtml` que a API devolve.
 *
 * Uma divergência consciente: as tags de i18n saem no idioma da INTERFACE, e não no do
 * documento (a API resolve no idioma do documento). É o mesmo que o `<cover>` do preview web
 * já faz, e vale só para a prévia dentro do editor.
 */
@Injectable({ providedIn: 'root' })
export class CoverFragmentBuilderService {
    /** A4 a 96dpi: a caixa que o `zoom` do `FIT_SCRIPT` precisa encaixar. */
    private static readonly PAGE_WIDTH_PX = 794;
    private static readonly PAGE_HEIGHT_PX = 1123;

    /**
     * Teto para o fundo da capa. O fetch do SVG não tinha limite nenhum: um CDN que aceita a
     * conexão e não responde deixava a folha em "carregando" para sempre, sem erro e sem retry.
     * Estourou, vale o fundo original (sem recolorir) — pior que a cor certa, melhor que nada.
     */
    private static readonly BACKGROUND_TIMEOUT_MS = 8000;

    /**
     * Quanto o aviso de "folha pronta" espera pelas imagens da capa antes de desistir. O `load`
     * da página é o sinal bom (ele espera as imagens), mas uma imagem que nunca chega não pode
     * segurar o esqueleto do app indefinidamente.
     */
    private static readonly READY_FALLBACK_MS = 2500;

    /**
     * Encaixa a folha na caixa do iframe. `zoom` e não `transform`: escala o layout de
     * verdade, então não sobra scroll (mesma escolha do composer da prévia).
     */
    private static readonly FIT_SCRIPT = `<script>
(function () {
	var fit = function () {
		var sheet = document.querySelector('.container-cover');
		if (!sheet) return;
		var root = document.documentElement;
		sheet.style.zoom = Math.min(
			root.clientWidth / ${CoverFragmentBuilderService.PAGE_WIDTH_PX},
			root.clientHeight / ${CoverFragmentBuilderService.PAGE_HEIGHT_PX}
		);
	};
	fit();
	window.addEventListener('resize', fit);

	// Avisa quem hospeda a folha que ela já está desenhada. Quem escuta é o webview da tela de
	// capa do app, que mantém o próprio esqueleto até aqui — sem este aviso ele revelaria a
	// página no \`onPageFinished\`, que dispara quando o Angular carrega e a capa ainda nem foi
	// buscada. Só existe dentro do app; no iframe do editor o canal não está definido.
	var told = false;
	var ready = function () {
		if (told) return;
		told = true;
		if (window.CoverPreviewReady && window.CoverPreviewReady.postMessage) {
			window.CoverPreviewReady.postMessage('ready');
		}
	};
	if (document.readyState === 'complete') ready();
	else window.addEventListener('load', ready);
	setTimeout(ready, ${CoverFragmentBuilderService.READY_FALLBACK_MS});
})();
</script>`;

    private readonly _http = inject(HttpClient);
    private readonly _translate = inject(TranslateService);

    /** Fundo já recolorido, por (url + cores): refazer o fetch a cada rebuild é caro. */
    private readonly _backgroundCache = new Map<string, string | null>();

    /**
     * A folha da capa inteira, pronta para um iframe `srcdoc`.
     *
     * Espelho do `buildCoverHtml` da API (o HTML que ela manda para o screenshot): mesmo
     * fragmento, mesma caixa de A4. O que diverge é só do palco para fora, porque lá a folha
     * É a imagem e aqui ela vive dentro de um iframe:
     *
     * - escala: lá `html { zoom: 2 }` deixa o PNG nítido no retina; aqui a folha se encaixa
     *   sozinha na caixa do iframe (`FIT_SCRIPT`), como o `fit()` do composer da prévia faz
     *   com as páginas do paged.js;
     * - moldura: a folha se centraliza e leva o próprio fundo branco e a sombra, e o iframe
     *   ocupa o palco inteiro, transparente. Dimensionar o iframe em A4 não serve: com o
     *   `max-width` do palco estreito a altura não acompanha, e a folha esticava.
     */
    public async buildPage(template: CoverTemplateEntity): Promise<string> {
        const fragment = await this.build(template);

        return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>
	* { margin: 0; padding: 0; box-sizing: border-box; }
	:root { --contentPrimary: #1f1f1f; --backgroundPrimary: #f5f5f5; --borderPrimary: #e0e0e0; }
	html, body { height: 100%; overflow: hidden; }
	/* A folha se centraliza e se pinta sozinha (a caixa do iframe é transparente e ocupa o
	   palco inteiro): assim o palco pode ter qualquer proporção sem deformar o A4. */
	body {
		display: flex;
		align-items: center;
		justify-content: center;
		font-family: Arial, Helvetica, sans-serif;
	}
	.container-cover {
		flex: none;
		width: 210mm;
		height: 297mm;
		background-color: #fff;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
		padding: 48px;
		background-repeat: no-repeat;
		background-origin: border-box;
		print-color-adjust: exact;
		-webkit-print-color-adjust: exact;
	}
</style>
</head>
<body>
${fragment}
${CoverFragmentBuilderService.FIT_SCRIPT}
</body>
</html>`;
    }

    /** O `<div class="container-cover">` com fundo e itens, sem `<html>` nem CSS externo. */
    public async build(template: CoverTemplateEntity): Promise<string> {
        const renderable = template as unknown as CoverRendererTemplate;

        const blocks = renderCoverItemsHtml(renderable, {
            platform: 'pdfConfigs',
            translate: tag => this._translate.instant(tag),
            // O HTML do PDF não define --contentPrimary; a var sozinha deixaria o texto preto
            // do navegador. Mesmo fallback da API.
            defaultTextColor: 'var(--contentPrimary, #1f1f1f)',
        });

        const background = await this._backgroundImage(renderable);
        const backgroundStyles = background
            ? `background-image: url('${background}'); ` +
              `background-size: ${coverBackgroundSizeCss(renderable.background?.boxFit)}; ` +
              `background-position: ${coverBackgroundPositionCss(renderable.background?.imageAlignment)};`
            : '';

        return `<div class="container-cover" style="${backgroundStyles}">\n${blocks.join('\n')}\n</div>`;
    }

    /**
     * O fundo vira DATA URL, nunca blob.
     *
     * A folha vive dentro de um iframe `srcdoc`, e um blob criado por esta página não é
     * endereçável lá dentro de forma confiável. Data URL atravessa. É também o formato que a
     * API usa, então o fundo é idêntico nos dois caminhos.
     */
    private async _backgroundImage(template: CoverRendererTemplate): Promise<string | null> {
        const url = template.background?.value?.trim();
        if (!url) return null;

        const key = JSON.stringify({ url, colors: template.background?.colors ?? [] });
        const cached = this._backgroundCache.get(key);
        if (cached !== undefined) return cached;

        const resolved = await this._fetchBackground(url, template);
        this._backgroundCache.set(key, resolved);
        return resolved;
    }

    private async _fetchBackground(
        url: string,
        template: CoverRendererTemplate,
    ): Promise<string | null> {
        // Só SVG é recolorido; qualquer outro formato entra pela própria URL.
        if (!url.toLowerCase().endsWith('.svg')) return url;

        try {
            // `text` e não `blob`: só o texto do SVG interessa, e ler texto de um Blob depende
            // de API que nem todo ambiente tem.
            const svg = await firstValueFrom(
                this._http
                    .get(this._sourceUrl(url), { responseType: 'text' })
                    .pipe(timeout(CoverFragmentBuilderService.BACKGROUND_TIMEOUT_MS)),
            );

            return `data:image/svg+xml;base64,${this._toBase64(
                applySvgColorModifications(svg, template.background?.colors),
            )}`;
        } catch {
            // Sem o SVG recolorido, o fundo original ainda é melhor do que capa sem fundo.
            return url;
        }
    }

    /**
     * Em localhost o CDN das capas não manda CORS, então o SVG só vem pelo proxy do dev server
     * (caminho relativo). Fora dali a URL do CDN vale como está. Mesma regra do `<cover>`.
     */
    private _sourceUrl(url: string): string {
        const isLocalDev =
            environment.API_BASE.includes('localhost') || window.location.hostname === 'localhost';
        if (!isLocalDev) return url;

        try {
            return new URL(url).pathname;
        } catch {
            return url;
        }
    }

    /** btoa quebra em acento; o SVG é UTF-8 e capas têm texto com acento. */
    private _toBase64(text: string): string {
        const bytes = new TextEncoder().encode(text);
        let binary = '';
        for (const byte of bytes) binary += String.fromCharCode(byte);

        return btoa(binary);
    }
}
